import { Directive, DirectiveBinding } from 'vue'
import { ElMessage } from 'element-plus'
import { i18n } from '@/i18n/index'

/** 权限指令支持的行为类型 */
export enum PermDirectiveType  {
  'hide' = 'hide',
  'alert' = 'alert',
  'disable' = 'disable',
}

/** 指令参数的对象格式（保持单权限，perm为字符串） */
interface PermDirectiveOption {
  perm: boolean   // 是否有权限
  type?: PermDirectiveType  // 行为类型，默认hide
}

/** 行为1：无权限时「隐藏元素」 */
function handleHide(el: HTMLElement, hasPermission: boolean) {
  if (!hasPermission) {
    el.classList.add('cm-role-perm-hide')
  } else {
    el.classList.remove('cm-role-perm-hide')
  }
}

/** 行为2：无权限时「点击提示无权限」 */
function handleAlert(el: HTMLElement, hasPermission: boolean) {
  // 1. 先移除旧的捕获事件（防止重复绑定）
  if ((el as any).__alertHandler) {
    el.removeEventListener('click', (el as any).__alertHandler, true)
    delete (el as any).__alertHandler
  }

  if (!hasPermission) {
    // 定义捕获阶段的事件处理函数（优先执行）
    const alertHandler = (e: Event) => {
      e.preventDefault()       // 阻止默认行为（如跳转、提交）
      e.stopPropagation()      // 阻止事件冒泡到父元素
      e.stopImmediatePropagation() // 阻止同一阶段其他事件执行
      ElMessage.warning(i18n.global.t('common.no_permission'))
    }
    // 3. 捕获阶段绑定事件（第三个参数true）
    el.addEventListener('click', alertHandler, true)
    el.onclick = alertHandler;
    // 保存 handler 引用，用于后续移除
    (el as any).__alertHandler = alertHandler
  } else {
    // 有权限时，恢复原始点击事件
    el.onclick = (el as any).__originalClick || null
  }
}

/** 行为3：无权限时「置灰+禁止点击」*/
function handleDisable(el: HTMLElement, hasPermission: boolean) {
  // 1. 先移除旧的捕获事件
  if ((el as any).__disableHandler) {
    el.removeEventListener('click', (el as any).__disableHandler, true)
    delete (el as any).__disableHandler
  }

  if (!hasPermission) {
    el.classList.add('cm-role-perm-disabled')

    // 定义捕获阶段的事件处理函数
    const disableHandler = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
    }
    // 3. 捕获阶段绑定事件
    el.addEventListener('click', disableHandler, true)
    el.onclick = disableHandler;
    (el as any).__disableHandler = disableHandler
    el.title = i18n.global.t('common.no_permission')
  } else {
    el.classList.remove('cm-role-perm-disabled')
    // 恢复原始点击事件和标题
    el.onclick = (el as any).__originalClick || null
    el.title = (el as any).__originalTitle || ''
  }
}

function elementInit (el: HTMLElement) {
  handleHide(el, true)
  handleDisable(el, true)
  handleAlert(el, true)
}

/** 指令核心逻辑：解析参数并分发到对应行为函数 */
function rolePermHandler(el: HTMLElement, binding: DirectiveBinding) {
  let hasPermission: boolean
  let type = PermDirectiveType.hide;
  // 保存原始点击事件（用于后续恢复）
  if ((el as any).__originalClick === undefined) {
    (el as any).__originalClick = el.onclick
  }
  if ((el as any).__originalTitle === undefined) {
    (el as any).__originalTitle = el.title
  }

  if (typeof binding.value === 'boolean') {
    hasPermission = binding.value
  } else if (typeof binding.value === 'object') {
    hasPermission = binding.value.perm
    type = binding.value.type || PermDirectiveType.hide
  } else {
    console.error('v-role-perm 指令参数格式错误，需为boolean或 { perm, type } 对象')
    return
  }

  elementInit(el)

  if (type === PermDirectiveType.hide) {
    handleHide(el, hasPermission)
  } else if (type === PermDirectiveType.alert) {
    handleAlert(el, hasPermission)
  } else if (type === PermDirectiveType.disable) {
    handleDisable(el, hasPermission)
  }
}

/** 组件卸载时清理内存（防止泄漏） */
export function rolePermUnmounted (el: HTMLElement) {
  elementInit(el)
}

/**
 * 角色权限指令（支持三种行为）：
 * - hide：无权限时隐藏元素（默认）
 * - alert：无权限时保留元素，点击弹提示
 * - disable：无权限时置灰元素并禁止点击，可在业务代码通过cm-role-perm-disabled类名定义样式
 *
 * @example
 * <button v-role-perm="cmRolePermMapFe.safemonitor_examination_stat">默认隐藏</button>
 * <button v-role-perm="{ perm: cmRolePermMapFe.safemonitor_examination_stat, type: PermDirectiveType.alert }" @click="handleSubmit">点击提示</button>
 * <button v-role-perm="{ perm: cmRolePermMapFe.safemonitor_examination_stat, type: PermDirectiveType.disable }">置灰禁用</button>
 */
export const rolePermDirective: Directive<
  HTMLElement,
  boolean | PermDirectiveOption
> = {
  mounted: rolePermHandler,
  updated: rolePermHandler,
  unmounted: rolePermUnmounted
}
